import {InfiniteData} from "@tanstack/query-core/src/types";
import {
    AddressItemType,
    GetItemCounts,
    PaginatedResponseType,
    PostageItemType,
    PostageUsageType,
    useInfinitePostage
} from "@twigsby/client-data";
import {AlertBanner, ColourSolid, ComboBoxControl, SelectItemType, useTranslation} from "@twigsby/frontend-framework";
import {GetArrayIndexByKeyValue} from "@twigsby/helpers";
import React, {useEffect, useState} from "react";

interface PostageControlType {
    activeAddress: AddressItemType | null;

    name: string;
    postageType: PostageUsageType;

    label?: string;
    placeholder?: string;
    minimal?: boolean;
    defaultValues?: Array<any>;
    singleSelection?: boolean;
    forwardValueCallback?: (value: number) => void;
    forwardValuesCallback?: (values: Array<any>) => void;
    forwardItemCallback?: (value: SelectItemType, checked: boolean) => void;

    alwaysEnabled?: boolean;

    loading?: boolean;
    disabled?: boolean;

    reset?: boolean;
    required?: boolean;
    requiredLabel?: string;
}

interface PostageControlInnerType extends PostageControlType {
    currentCountryCode: string;
}

const PostageControl = (props: PostageControlType) => {
    const [ currentCountryCode, setCurrentCountryCode ] = useState<string>(props.activeAddress?.object.countryCode ?? 'GB');

    useEffect(() => {
        if(props.activeAddress && props.activeAddress.object.countryCode !== currentCountryCode) {
            setCurrentCountryCode(props.activeAddress.object.countryCode);
        }
    }, [props.activeAddress]);

    return (
        <PostageControlInner
            {...props}
            currentCountryCode={currentCountryCode}
        />
    )
}

const PostageControlInner = (props: PostageControlInnerType) => {
    const { t } = useTranslation();

    const { status,
        data,
        error,
        dataUpdatedAt,
        isFetchingNextPage,
        fetchNextPage,
        hasNextPage
    } = useInfinitePostage(props.currentCountryCode, props.postageType);

    const [ postageDefault, setPostageDefault ] = useState<Array<any> | undefined>(props.defaultValues);
    const [ postageOptions, setPostageOptions ] = useState<Array<SelectItemType>>([]);
    const [ postageItemCounts, setPostageItemCounts] = useState<any>({ loaded: 0, total: 0});
    const [ postageError, setPostageError ] = useState<string>('');

    const MapPostageOptions = (data: InfiniteData<PaginatedResponseType>) => {
        setPostageItemCounts(GetItemCounts(data));

        let filteredPostage = data?.pages.map((page: any) => page?.data && page.data.map((postage: PostageItemType, index: number) => {
            return { 'label': postage.label, 'value': postage.value }
        }));

        filteredPostage = filteredPostage.flat();

        if(props.defaultValues) {
            const valueExists = GetArrayIndexByKeyValue(filteredPostage, 'value', props.defaultValues[0]);

            if(valueExists === -1) {
                setPostageError('The selected Postage Option is not available for this address');
                setPostageDefault(undefined);
            } else {
                HandleItemsChanged(props.defaultValues[0], true);
                setPostageError('');
                setPostageDefault(props.defaultValues);
            }

            setPostageDefault(valueExists === -1 ? undefined : props.defaultValues);
        } else {
            setPostageDefault(undefined);
        }

        setPostageOptions(filteredPostage);
    };

    const HandleValueChanged = (e: any) => {
        if(props.forwardValuesCallback) {
            props.forwardValuesCallback(e);
        }

        if(props.forwardValueCallback) {
            props.forwardValueCallback(e);
        }
    }

    const HandleItemsChanged = (e: number, checked: boolean) => {
        if(props.forwardItemCallback) {
            const postageItem = data?.pages.map((page) => page?.data && page.data.filter((postage) => {
                return postage.value === e;
            }))[0][0];

            if(postageItem) {
                props.forwardItemCallback(postageItem, checked);
            }
        }
    }

    useEffect(() => {
        if(data) {
            MapPostageOptions(data);
        }
    }, [dataUpdatedAt, props.currentCountryCode]);

    if(status === 'error') {
        return <AlertBanner title={error?.message} colourMod={ColourSolid.Alert} />
    }

    return (
        <>
            <ComboBoxControl
                name={props.name}
                placeholder={props.placeholder || t('controls.postagePlaceholder')}
                data={postageOptions}
                defaultValues={postageError === '' && postageOptions.length > 0 ? postageDefault : undefined}
                loading={status === 'loading' || !postageOptions || props.loading}
                valueType={'number'}
                isFetchingNextPage={isFetchingNextPage}
                fetchNextPage={fetchNextPage}
                hasNextPage={hasNextPage}
                itemCounts={postageItemCounts}
                singleSelection={props.singleSelection}
                forwardItemCallback={(e, checked) => HandleItemsChanged(e.value, checked) }
                forwardValueCallback={(e) => HandleValueChanged(e) }
                forwardValuesCallback={(e) => HandleValueChanged(e) }
                disabled={props.disabled || (!props.activeAddress && !props.alwaysEnabled)}
                reset={props.reset}
            />
            {postageError && (
                <AlertBanner title={postageError} colourMod={ColourSolid.Alert}/>
            )}
        </>
    )
}

export { PostageControl }
